import React, { Component, createRef, Fragment } from 'react';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, isEnumValid, isMobileResolution, shallowCompare } from '@philips-internal/oswald-ui-utils';
import { TimeFieldMode, TimeFieldPeriod, TimeFieldPickerIndex, TimeFieldSelectionMode } from '../time-field-constants.js';
import { formatTime, formatTimeNumber } from '../time-field-utils.js';
import './time-picker.css';
const TIME_MODE_DIFFERENCE = 12;
const TIME_UNIT_HALF_HEIGHT = 24;
const TIME_UNIT_SCROLL_AREA_SPACINGS = 240;
class TimePicker extends Component {
    constructor() {
        super(...arguments);
        this.highlightAreaRef = createRef();
        this.state = {
            firstTouch: 0,
            hours: '',
            lastTouch: 0,
            minutes: '',
            period: TimeFieldPeriod.AM,
        };
        this.getActualHours = () => {
            const { timeMode } = this.props;
            const { hours, period } = this.state;
            const hourAsNumber = Number(hours);
            const isDifferenceAlreadyApplied = hourAsNumber === 0 || hourAsNumber > 12;
            const applyHoursDifference = !isDifferenceAlreadyApplied && timeMode === TimeFieldMode.TWELVE_HOURS;
            if (!applyHoursDifference) {
                return hours;
            }
            if (period === TimeFieldPeriod.AM) {
                const difference = hourAsNumber === 12 ? -TIME_MODE_DIFFERENCE : 0;
                return formatTimeNumber(hourAsNumber + difference);
            }
            const difference = hourAsNumber === 12 ? 0 : TIME_MODE_DIFFERENCE;
            return formatTimeNumber(hourAsNumber + difference);
        };
        this.onSelectTimeValue = () => {
            const { index, onChange, timeMode } = this.props;
            const { minutes } = this.state;
            const value = formatTime({ hours: this.getActualHours(), minutes, timeMode });
            onChange({ index, value });
        };
        this.scrollIntoView = (element) => {
            element.scrollIntoView({
                block: 'center',
                inline: 'center',
            });
        };
        this.getClosestElement = (elements) => {
            return elements.reduce((previous, current) => {
                const previousY = previous.getBoundingClientRect().y;
                const currentY = current.getBoundingClientRect().y;
                const y = this.highlightAreaRef.current?.getBoundingClientRect().y;
                const center = Number(y) + TIME_UNIT_HALF_HEIGHT;
                const isCloserElement = Math.abs(currentY - center) < Math.abs(previousY - center);
                return isCloserElement ? current : previous;
            });
        };
        this.onTouchStart = (event) => {
            this.setState({
                firstTouch: event.nativeEvent.touches[0].clientY,
                lastTouch: event.nativeEvent.touches[0].clientY,
            });
        };
        this.onTouchMove = (event) => {
            const { firstTouch, lastTouch } = this.state;
            const { currentTarget } = event;
            const realScrollHeight = currentTarget.scrollHeight - TIME_UNIT_SCROLL_AREA_SPACINGS;
            if (currentTarget.scrollTop >= realScrollHeight) {
                return currentTarget.scrollTo(0, 1);
            }
            if (currentTarget.scrollTop <= 0) {
                return currentTarget.scrollTo(0, realScrollHeight - TIME_UNIT_HALF_HEIGHT);
            }
            const isFirstTouchGreaterThanLast = firstTouch > lastTouch;
            const moveDifference = isFirstTouchGreaterThanLast
                ? firstTouch - lastTouch
                : lastTouch - firstTouch;
            const newY = isFirstTouchGreaterThanLast
                ? currentTarget.scrollTop + moveDifference
                : currentTarget.scrollTop - moveDifference;
            currentTarget.scrollTo(0, newY);
            this.setState({ lastTouch: event.nativeEvent.touches[0].clientY });
        };
        this.onTouchEnd = (event) => {
            const isMinute = event.currentTarget.getAttribute('data-unit-container') === 'minutes';
            const searchTerm = isMinute ? 'minute' : 'hour';
            const elements = document.querySelectorAll(`[data-unit="${searchTerm}"]`);
            const closest = this.getClosestElement([...elements]);
            const value = closest.getAttribute('data-value');
            if (!value) {
                return;
            }
            const updateValue = isMinute ? { minutes: value } : { hours: value };
            this.scrollIntoView(closest);
            this.setState((prevState) => {
                const { hours = prevState.hours, minutes = prevState.minutes } = updateValue;
                return {
                    firstTouch: 0,
                    hours,
                    lastTouch: 0,
                    minutes,
                };
            });
        };
        this.getHoursContainer = () => {
            const { hoursArray } = this.props;
            const { hours } = this.state;
            const elements = hoursArray.map((hour) => {
                const className = classNames({
                    'c-time-picker__hour': true,
                    'c-time-picker__hour--selected': hour === hours,
                });
                const key = `tws-time-picker-hour-${hour}`;
                const onClick = (event) => {
                    const { currentTarget } = event;
                    this.setState({ hours: hour });
                    this.scrollIntoView(currentTarget);
                };
                return (React.createElement("div", { className: className, "data-unit": "hour", "data-value": hour, key: key, onClick: onClick }, hour));
            });
            return (React.createElement("div", { className: "c-time-picker__hours u-scrollbar", "data-unit-container": "hours", onTouchEnd: this.onTouchEnd, onTouchMove: this.onTouchMove, onTouchStart: this.onTouchStart }, elements));
        };
        this.getMinutesContainer = () => {
            const { minutesArray } = this.props;
            const { minutes } = this.state;
            const elements = minutesArray.map((minute) => {
                const className = classNames({
                    'c-time-picker__minute': true,
                    'c-time-picker__minute--selected': minute === minutes,
                });
                const key = `tws-time-picker-minute-${minute}`;
                const onClick = (event) => {
                    const { currentTarget } = event;
                    this.setState({ minutes: minute });
                    this.scrollIntoView(currentTarget);
                };
                return (React.createElement("div", { className: className, "data-unit": "minute", "data-value": minute, key: key, onClick: onClick }, minute));
            });
            return (React.createElement("div", { className: "c-time-picker__minutes u-scrollbar", "data-unit-container": "minutes", onTouchEnd: this.onTouchEnd, onTouchMove: this.onTouchMove, onTouchStart: this.onTouchStart }, elements));
        };
        this.getPeriodContainer = () => {
            const { timeMode } = this.props;
            const { period } = this.state;
            if (timeMode !== TimeFieldMode.TWELVE_HOURS) {
                return null;
            }
            const onClick = (period) => this.setState({ period });
            const getClassName = (value) => classNames({
                'c-time-picker__period': true,
                'c-time-picker__period--selected': period === value,
            });
            return (React.createElement("div", { className: "c-time-picker__periods u-scrollbar" },
                React.createElement("div", { className: getClassName(TimeFieldPeriod.AM), onClick: () => onClick(TimeFieldPeriod.AM) }, TimeFieldPeriod.AM),
                React.createElement("div", { className: getClassName(TimeFieldPeriod.PM), onClick: () => onClick(TimeFieldPeriod.PM) }, TimeFieldPeriod.PM)));
        };
        this.renderHighlightArea = () => {
            const { selectionMode } = this.props;
            if (!isMobileResolution() || selectionMode === TimeFieldSelectionMode.PREDEFINED) {
                return null;
            }
            return (React.createElement(Fragment, null,
                React.createElement("div", { className: "c-picker-highlight-area", ref: this.highlightAreaRef }),
                React.createElement("span", { className: "c-picker-highlight-separator" }, ":")));
        };
        this.renderFreePicker = () => {
            return (React.createElement(Fragment, null,
                this.renderHighlightArea(),
                this.getHoursContainer(),
                this.getMinutesContainer(),
                this.getPeriodContainer()));
        };
        this.renderPredefinedToken = (tokenTime, index) => {
            const { timeMode } = this.props;
            const { hours, minutes } = this.state;
            const [tokenHours, tokenMinutes] = tokenTime.split(':');
            const formattedTokenTime = formatTime({ hours: tokenHours, minutes: tokenMinutes, timeMode });
            const isSelected = hours === tokenHours && minutes === tokenMinutes;
            const onClick = () => {
                const tokenHoursAsNumber = Number(tokenHours);
                const isPM = tokenHoursAsNumber === 0 || tokenHoursAsNumber >= 12;
                this.setState({
                    hours: tokenHours,
                    minutes: tokenMinutes,
                    period: isPM ? TimeFieldPeriod.PM : TimeFieldPeriod.AM,
                });
            };
            const className = classNames({
                'c-time-picker__token': true,
                'c-time-picker__token--selected': isSelected,
            });
            const key = `tws-time-picker-token-${index}`;
            return (React.createElement("div", { className: className, key: key, onClick: onClick }, formattedTokenTime));
        };
        this.renderPredefinedPicker = () => {
            const { data } = this.props;
            if (!data.length) {
                return null;
            }
            return (React.createElement("div", { className: "c-time-picker__tokens-wrapper u-scrollbar" }, data.map(this.renderPredefinedToken)));
        };
        this.renderPickerTitle = () => {
            const { fromExpression, index, interval, selectionMode, untilExpression } = this.props;
            if (!interval || !isMobileResolution() || selectionMode === TimeFieldSelectionMode.PREDEFINED) {
                return null;
            }
            const expression = index === TimeFieldPickerIndex.FROM ? fromExpression : untilExpression;
            return (React.createElement("div", { className: "c-time-picker-title" }, MessageService.translate(expression)));
        };
    }
    componentDidMount() {
        const { time, timeMode } = this.props;
        const currentTime = time ? time.match(/(\d+|[AP][M])/g) : null;
        const defaultHours = timeMode === TimeFieldMode.TWELVE_HOURS ? '01' : '00';
        const [initialHours = defaultHours, initialMinutes = '00', initialPeriod = TimeFieldPeriod.AM] = currentTime ?? [];
        this.setState({
            hours: initialHours,
            minutes: initialMinutes,
            period: initialPeriod,
        }, () => {
            const hourToScroll = document.querySelector(`[data-unit="hour"][data-value="${initialHours}"]`);
            const minuteToScroll = document.querySelector(`[data-unit="minute"][data-value="${initialMinutes}"]`);
            if (hourToScroll) {
                this.scrollIntoView(hourToScroll);
            }
            if (minuteToScroll) {
                this.scrollIntoView(minuteToScroll);
            }
        });
    }
    componentDidUpdate(_prevProps, prevState) {
        const shallowCompareState = ['hours', 'minutes', 'period'];
        if (shallowCompare(prevState, this.state, shallowCompareState)) {
            this.onSelectTimeValue();
        }
    }
    render() {
        const { index, selectionMode, timeMode } = this.props;
        const timePickerClassName = classNames({
            'c-time-picker': true,
            'c-time-picker--from': index === TimeFieldPickerIndex.FROM,
            'c-time-picker--until': index === TimeFieldPickerIndex.UNTIL,
            [`c-time-picker--${selectionMode}`]: isEnumValid(TimeFieldSelectionMode, selectionMode),
            [`c-time-picker--${timeMode}-hours`]: isEnumValid(TimeFieldMode, timeMode),
        });
        return (React.createElement("div", { className: timePickerClassName },
            this.renderPickerTitle(),
            React.createElement("div", { className: "c-time-picker__pick-area" }, selectionMode === TimeFieldSelectionMode.FREE ? this.renderFreePicker() : this.renderPredefinedPicker())));
    }
}
export { TimePicker, };
//# sourceMappingURL=time-picker.js.map